import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { generateInviteCode, revokeInviteCode, INVITE_ROLE_OPTS, GUEST_ROLES } from '../../../utils/inviteUtils';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };
const CURRENT_USER_NAME = () => {
  try { return getAuth(getApp()).currentUser?.displayName || 'Admin'; } catch (_) { return 'Admin'; }
};
const CURRENT_UID = () => {
  try { return getAuth(getApp()).currentUser?.uid || ''; } catch (_) { return ''; }
};

const S = {
  bg: 'var(--smc-bg, #070c1a)', panel: 'var(--smc-panel, #0d1526)', input: 'var(--smc-deep, #060b18)',
  border: 'var(--smc-bd, rgba(255,255,255,0.08))', faint: 'var(--smc-hov, rgba(255,255,255,0.04))',
  text: 'var(--smc-tx, #d9e2ff)', muted: 'var(--smc-tx2, #8892a4)', dim: 'var(--smc-tx3, #4d5a6e)',
  primary: 'var(--smc-ac, #65d4fa)', green: 'var(--smc-gn, #34d399)', red: 'var(--smc-rd, #f87171)', yellow: 'var(--smc-am, #fbbf24)',
  mono: "'Share Tech Mono','Courier New',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

const roleColor = id => INVITE_ROLE_OPTS.find(r => r.id === id)?.color || '#64748b';
const roleLabel = id => INVITE_ROLE_OPTS.find(r => r.id === id)?.label || id;

function fmtTs(ts) {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function daysLeft(ts) {
  if (!ts?.toDate) return null;
  const d = Math.ceil((ts.toDate() - Date.now()) / 86400000);
  return d;
}

export default function InviteLinksTab({ guestOnly = false }) {
  const db = DB();
  const roleOpts = guestOnly
    ? INVITE_ROLE_OPTS.filter(r => GUEST_ROLES.has(r.id))
    : INVITE_ROLE_OPTS;
  const [invites,     setInvites]     = useState([]);
  const [role,        setRole]        = useState(guestOnly ? 'guest' : 'mp');
  const [maxUses,     setMaxUses]     = useState(1);
  const [expiresHrs,  setExpiresHrs]  = useState(72);
  const [busy,        setBusy]        = useState(false);
  const [revoking,    setRevoking]    = useState(null);
  const [newCode,     setNewCode]     = useState('');
  const [copied,      setCopied]      = useState('');
  const [err,         setErr]         = useState('');

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'invite_codes'), orderBy('createdAt', 'desc')),
      snap => setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return unsub;
  }, [db]);

  async function create() {
    setBusy(true); setErr(''); setNewCode('');
    try {
      const code = await generateInviteCode({
        role, maxUses: Number(maxUses), expiresHours: Number(expiresHrs),
        createdBy: CURRENT_UID(), createdByName: CURRENT_USER_NAME(),
      });
      setNewCode(code);
    } catch (ex) { setErr(ex.message || 'Failed to generate invite'); }
    setBusy(false);
  }

  async function revoke(code) {
    setRevoking(code);
    try { await revokeInviteCode(code); } catch (ex) { setErr(ex.message || 'Failed to revoke'); }
    setRevoking(null);
  }

  function copy(text) {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(''), 2000);
  }

  const inviteLink = code => `${window.location.origin}/?invite=${code}`;

  const active   = invites.filter(i => i.active && (daysLeft(i.expiresAt) ?? 1) > 0 && i.usedCount < i.maxUses);
  const inactive = invites.filter(i => !i.active || (daysLeft(i.expiresAt) ?? 1) <= 0 || i.usedCount >= i.maxUses);

  return (
    <div style={{ padding: '18px 22px', overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>

      {/* Create form */}
      <div style={{ background: S.panel, border: `1px solid ${S.border}`, padding: '18px 20px', marginBottom: 24 }}>
        <div style={{ fontFamily: S.mono, fontSize: 10, color: S.primary, letterSpacing: 2, marginBottom: 16 }}>
          GENERATE INVITE LINK
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: 160 }}>
            <div style={{ fontFamily: S.mono, fontSize: 8, color: S.dim, letterSpacing: 1, marginBottom: 5, textTransform: 'uppercase' }}>Role</div>
            <select value={role} onChange={e => setRole(e.target.value)}
              style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: roleColor(role),
                fontFamily: S.mono, fontSize: 11, padding: '8px 10px', outline: 'none', cursor: 'pointer' }}>
              {roleOpts.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 80 }}>
            <div style={{ fontFamily: S.mono, fontSize: 8, color: S.dim, letterSpacing: 1, marginBottom: 5, textTransform: 'uppercase' }}>Max Uses</div>
            <input type="number" value={maxUses} min={1} max={50}
              onChange={e => setMaxUses(e.target.value)}
              style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: S.text,
                fontFamily: S.mono, fontSize: 11, padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1, minWidth: 100 }}>
            <div style={{ fontFamily: S.mono, fontSize: 8, color: S.dim, letterSpacing: 1, marginBottom: 5, textTransform: 'uppercase' }}>Expires (hrs)</div>
            <select value={expiresHrs} onChange={e => setExpiresHrs(e.target.value)}
              style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: S.text,
                fontFamily: S.mono, fontSize: 11, padding: '8px 10px', outline: 'none', cursor: 'pointer' }}>
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
              <option value={72}>72 hours</option>
              <option value={168}>7 days</option>
              <option value={720}>30 days</option>
            </select>
          </div>
          <button onClick={create} disabled={busy}
            style={{ padding: '9px 20px', fontFamily: S.mono, fontSize: 10, fontWeight: 700,
              letterSpacing: 1, background: busy ? 'transparent' : S.primary,
              border: `1px solid ${busy ? S.border : S.primary}`,
              color: busy ? S.dim : '#060b18', cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {busy ? 'GENERATING…' : '+ CREATE LINK'}
          </button>
        </div>

        {err && (
          <div style={{ fontFamily: S.mono, fontSize: 9, color: S.red, marginTop: 10,
            padding: '6px 10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
            {err}
          </div>
        )}

        {newCode && (
          <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(52,211,153,0.07)',
            border: '1px solid rgba(52,211,153,0.25)' }}>
            <div style={{ fontFamily: S.mono, fontSize: 8, color: S.green, letterSpacing: 2, marginBottom: 8 }}>
              INVITE LINK CREATED
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <code style={{ flex: 1, fontFamily: S.mono, fontSize: 10, color: S.text,
                background: S.input, padding: '6px 10px', wordBreak: 'break-all' }}>
                {inviteLink(newCode)}
              </code>
              <button onClick={() => copy(inviteLink(newCode))}
                style={{ padding: '6px 12px', fontFamily: S.mono, fontSize: 9, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: copied === inviteLink(newCode) ? 'rgba(52,211,153,0.15)' : 'transparent',
                  border: `1px solid ${copied === inviteLink(newCode) ? S.green : S.border}`,
                  color: copied === inviteLink(newCode) ? S.green : S.dim }}>
                {copied === inviteLink(newCode) ? '✓ COPIED' : 'COPY'}
              </button>
            </div>
            <div style={{ fontFamily: S.mono, fontSize: 8, color: S.dim, marginTop: 6 }}>
              Code: <span style={{ color: S.primary }}>{newCode}</span> ·
              Role: <span style={{ color: roleColor(role) }}>{roleLabel(role)}</span> ·
              Max uses: {maxUses} · Expires in {expiresHrs}h
            </div>
          </div>
        )}
      </div>

      {/* Active invites */}
      {active.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: 2, marginBottom: 12, textTransform: 'uppercase' }}>
            Active Invites ({active.length})
          </div>
          {active.map(inv => {
            const dl = daysLeft(inv.expiresAt);
            const link = inviteLink(inv.code);
            return (
              <div key={inv.id} style={{ background: S.panel, border: `1px solid ${S.border}`,
                padding: '12px 16px', marginBottom: 8, display: 'flex', gap: 14,
                alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontFamily: S.mono, fontSize: 11, color: S.primary, fontWeight: 700 }}>{inv.code}</span>
                    <span style={{ fontFamily: S.mono, fontSize: 9, color: roleColor(inv.role),
                      background: `${roleColor(inv.role)}18`, border: `1px solid ${roleColor(inv.role)}44`,
                      padding: '1px 8px', borderRadius: 999 }}>
                      {roleLabel(inv.role)}
                    </span>
                  </div>
                  <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>
                    Uses: {inv.usedCount}/{inv.maxUses} ·
                    Expires: <span style={{ color: dl !== null && dl <= 2 ? S.red : dl !== null && dl <= 7 ? S.yellow : S.dim }}>
                      {dl !== null ? (dl <= 0 ? 'Expired' : `${dl}d left`) : '—'}
                    </span> ·
                    By {inv.createdByName || 'Admin'} · {fmtTs(inv.createdAt)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => copy(link)}
                    style={{ padding: '5px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                      background: copied === link ? 'rgba(52,211,153,0.1)' : 'transparent',
                      border: `1px solid ${copied === link ? S.green : S.border}`,
                      color: copied === link ? S.green : S.dim }}>
                    {copied === link ? '✓' : 'COPY'}
                  </button>
                  <button onClick={() => revoke(inv.code)} disabled={revoking === inv.code}
                    style={{ padding: '5px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                      background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: S.red }}>
                    {revoking === inv.code ? '…' : 'REVOKE'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inactive / used invites */}
      {inactive.length > 0 && (
        <div>
          <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: 2, marginBottom: 12, textTransform: 'uppercase', opacity: 0.6 }}>
            Expired / Used ({inactive.length})
          </div>
          {inactive.map(inv => (
            <div key={inv.id} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${S.faint}`,
              padding: '10px 16px', marginBottom: 6, opacity: 0.6,
              display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
                  <span style={{ fontFamily: S.mono, fontSize: 11, color: S.dim }}>{inv.code}</span>
                  <span style={{ fontFamily: S.mono, fontSize: 8, color: S.dim,
                    border: `1px solid ${S.faint}`, padding: '1px 6px', borderRadius: 999 }}>
                    {roleLabel(inv.role)}
                  </span>
                  <span style={{ fontFamily: S.mono, fontSize: 8, color: S.dim }}>
                    {!inv.active ? 'REVOKED' : inv.usedCount >= inv.maxUses ? `USED (${inv.usedCount}/${inv.maxUses})` : 'EXPIRED'}
                  </span>
                </div>
                <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim }}>
                  By {inv.createdByName || 'Admin'} · {fmtTs(inv.createdAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {invites.length === 0 && !newCode && (
        <div style={{ textAlign: 'center', padding: '48px 24px', fontFamily: S.mono, fontSize: 11, color: S.dim }}>
          No invite links yet. Create one above.
        </div>
      )}
    </div>
  );
}
